/**
 * Posts validation: ensures each post has a valid job title (with a configured labor
 * coefficient matching the selected interim agency), a recourse case reason and a
 * justification.
 */
import { JobTitle } from '../../../../../api/job-titles'
import { getDocInArray } from '../../../../../utils/documents'
import { getJobTitleInterimAgencySettings } from '../../../../../utils/job-titles'
import {
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormErrors,
} from '../workers-request-form.types'

/**
 * Dependencies required to validate posts (not derivable from values alone).
 */
export interface ValidatePostsDeps {
  jobTitles: JobTitle[]
}

/**
 * Validates the posts array.
 *
 * Error keys use the flat path convention used by Formik (`posts.${index}.field`)
 * so that they can be consumed directly by the relevant `<Field>` components.
 *
 * @param values - Current Formik values.
 * @param deps - External data needed (notably the jobTitles cache).
 * @returns Partial errors object.
 */
export const validatePosts = (
  values: WorkersRequestCreateUpdateFormValues,
  deps: ValidatePostsDeps,
): WorkersRequestFormErrors => {
  const errors: WorkersRequestFormErrors = {}

  // At least one post is required.
  if (values.posts.length === 0) {
    errors['posts'] = 'Merci de remplir au moins un poste à pourvoir'
  }

  for (let index = 0; index < values.posts.length; index++) {
    const post = values.posts[index]

    // Post job title + labor coefficient check with the selected interim agency.
    if (!post?.jobTitle) {
      // @ts-expect-error -- flat dotted path not covered by FormikErrors type.
      errors[`posts.${index}.jobTitle`] = 'Veuillez choisir un métier'
    } else if (post?.jobTitle) {
      const jobTitle = getDocInArray<JobTitle>(deps.jobTitles, post.jobTitle)
      if (jobTitle && values.interimAgency) {
        const laborCoefficient = getJobTitleInterimAgencySettings(jobTitle, values.interimAgency)
        if (
          !laborCoefficient ||
          laborCoefficient.coefficients.delegationValue === null ||
          laborCoefficient.coefficients.managementValue === null
        ) {
          // @ts-expect-error -- flat dotted path not covered by FormikErrors type.
          errors[`posts.${index}.jobTitle`] =
            `Le coefficient main d'oeuvre n'est pas configuré entre ce poste et l'agence.`
        }
      }
    }

    // Recourse case reason.
    if (!post?.recourseCase.reason) {
      // @ts-expect-error -- flat dotted path not covered by FormikErrors type.
      errors[`posts.${index}.recourseCase.reason`] = 'Veuillez choisir un Cas de Recours'
    }

    // Recourse case justification.
    if (!post?.recourseCase.justification) {
      // @ts-expect-error -- flat dotted path not covered by FormikErrors type.
      errors[`posts.${index}.recourseCase.justification`] =
        'Veuillez remplir une Justification pour le Cas de Recours'
    }
  }

  return errors
}
